import { useState, useRef, useEffect } from 'react';
import { Link } from "react-router-dom";
import { DayBar } from '../components';
import { fetchSingleMeditation, mapSingleMeditationData, toggleFavorite } from '../utilities';
import {
    TabPanel,
    MobileNavigation,
    CalendarWhite,
    HomeIcon,
    ElementEqualIcon,
    BookOpenWhite,
    UsersIcon,
    HelpIcon,
    HomeIconFilled,
    CalendarIconFilled,
    FavoritesIcon,
    FavoritesIconFilled,
    MoreIcon,
    MoreIconFilled,
    LeftArrowIcon,
    HeartIcon,
    SunIcon,
    PlayIcon,
    VoiceSquare,
    AudioPlayer,
} from '../components';
import TrinityAudioPlayer from './TrinityAudioPlayer';
import { Skeleton } from "../components/ui/skeleton";
import { useLocation } from 'react-router-dom';
import { SidebarLeft } from '../templates';
import { useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import useLiturgicalEvents from '../hooks/useLiturgicalEvents';
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger
} from "src/components/ui/tabs"

const Meditation = () => {
    const [isChecked, setIsChecked] = useState(false);
    const [loading, setLoading] = useState(true);
    const [showPlayer, setShowPlayer] = useState(false);
    const [showTrinity, setShowTrinity] = useState(false);
    const [activePlayerIndex, setActivePlayerIndex] = useState(null); // Track active audio player
    const [progress, setProgress] = useState(0);
    const audioRef = useRef(null);
    const [progressPercentage, setProgressPercentage] = useState(0);
    const [meditationData, setMeditationData] = useState(null)
    const { id } = useParams();
    const [isLoading, setIsLoading] = useState(true)
    const today = new Date();
    const { isDataLoading, eventData } = useLiturgicalEvents(
        today.getFullYear(),
        today.getMonth() + 1,
        today.getDate()
    );
    const isValidAudioUrl = (url) => typeof url === "string" && /\.(mp3|ogg|wav)$/i.test(url);
    // Ensure skeleton stays until API data is loaded
    useEffect(() => {
        if (!isDataLoading && eventData) {
            setIsLoading(false); // Hide skeleton only when API data is fully loaded
        }
    }, [isDataLoading, eventData]);
    // Extract relevant event details
    const firstEventKey = eventData ? Object.keys(eventData)[0] : null;
    const firstEvent = firstEventKey ? eventData[firstEventKey] : null;
    // Ensure no flickering or static data issues
    const dayData = isDataLoading
        ? {} //Show skeleton while loading (NO STATIC DATA)
        : {
            date: firstEvent?.date
                ? new Date(firstEvent.date * 1000).toLocaleDateString("en-US", {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                })
                : "",
            title: firstEvent?.name || "",
            tag: firstEvent?.liturgicalSeasonLcl || "",
            label: firstEvent ? "Today" : "",
            category: "Easter",
            description: firstEvent?.eventData?.description || '',
        };

    useEffect(() => {
        if (!id) {
            console.error("No id provided for meditation.");
            return;
        }
        setLoading(true);
        const loadMeditation = async () => {
            try {
                console.log(`Fetching meditation for id: ${id}`);
                // const encodedid = encodeURIComponent(id.toLowerCase().replace(/\s+/g, '-'));
                // console.log(`Fetching meditation for id: ${encodedid}`);
                const response = await fetchSingleMeditation(id);
                if (!response) {
                    console.error("No Data Returned from api for id ", id);
                    setMeditationData(null);
                    return;
                }
                const mapped = mapSingleMeditationData(response);
                setMeditationData(mapped);
            } catch (error) {
                console.error("Failed to fetch meditation data:", error);
                setMeditationData(null); // Ensure state resets on failure
            } finally {
                setLoading(false);
            }
        };
        loadMeditation();
    }, [id]);

    const handlePlay = () => {
        if (isValidAudioUrl(meditationData?.audio)) {
            setShowTrinity(false);
            setShowPlayer(true);
            setTimeout(() => audioRef.current?.play(), 100);
        } else {
            setShowPlayer(false);
            setShowTrinity(true);
        }
    };

    // Handle play button click
    const handlePlayClick = (index) => {
        if (activePlayerIndex === index) {
            setActivePlayerIndex(null); // Stop current player
            if (audioRef.current) {
                audioRef.current.pause(); // Pause the audio
            }
        } else {
            setActivePlayerIndex(index); // Activate the selected player
            setTimeout(() => {
                if (audioRef.current) {
                    audioRef.current.play(); // Start playing the audio
                }
            }, 0); // Ensure state updates before playback
        }
    };
    // Handle closing the player
    const handleCloseAudioPlayer = () => {
        setActivePlayerIndex(null); // Close the player
        if (audioRef.current) {
            audioRef.current.pause(); // Pause the audio
        }
    };
    const handleProgressUpdate = (progress) => {
        setProgressPercentage(progress);
    };

    const handleToggleFavorite = async (event, id) => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        if (!meditationData || !id) return;
        const currentFavorite = meditationData.favorite
        try {
            const updatedFavoriteStatus = !meditationData.favorite;
            const catechesisId = String(meditationData.id);
            // Step 1: Optimistically update UI
            setMeditationData((prevData) => ({
                ...prevData,
                favorite: updatedFavoriteStatus,
            }));
            // Step 3: Call API (Do NOT reload everything)
            await toggleFavorite('meditations', catechesisId, false, currentFavorite);
            window.dispatchEvent(new Event('favorites-updated'));
        } catch (error) {
            console.error("Failed to update favorite:", error);
            // Step 4: If API call fails, revert UI
            setMeditationData((prevData) => ({
                ...prevData,
                favorite: !prevData.favorite,
            }));
        }
    };
    const audioData = [
        {
            title: "Play Audio",
            src: "http://webaudioapi.com/samples/audio-tag/chrono.mp3",
            excerpt: "English Language Audio",
        },
    ];

    return (
        <div className='relative w-full bg-white-50'>
            <div className='xl:px-12 px-6 pt-7.5 flex justify-between'>
                <div className='flex'>
                    <Link to="/" className='flex items-center justify-center rounded-full bg-white-50 md:h-11 md:w-11 h-9 w-9 group'>
                        {loading ? <Skeleton className="h-7 w-7 rounded-full" /> : <LeftArrowIcon className="text-gray-700 group-hover:text-gold-500" />}
                    </Link>
                    <div className='flex flex-col max-w-2xl pl-4'>
                        {/* <p className='text-base text-left font-normal text-gray-600 font-redhat leading-6.5'> {loading ? <Skeleton className="h-4 w-38 rounded" /> : "May 1, 2024"}</p>
            <h5 className="text-xl font-bold text-gray-900 underline leading-26 font-cmr"> {loading ? <Skeleton className="h-4 w-40 rounded mt-1" /> : "Liturgical Day"}</h5> */}
                        <DayBar isLoading={loading} title={dayData?.title} date={dayData?.date} hideIcon={true} />
                        {console.log(dayData?.title, "=======>>>>>>>>>>")
                        }
                    </div>
                </div>
                <div>
                    <button
                        key={meditationData?.favorite}
                        type="button"
                        onClick={(e) => meditationData && handleToggleFavorite(e, meditationData.id)}
                        disabled={!meditationData}
                        className="flex items-center justify-center rounded-full bg-gold-50 md:h-11 md:w-11 h-9 w-9 group">
                        {
                            meditationData ? (
                                <FavoritesIcon filled={Boolean(meditationData?.favorite)} className={`text-gray-700 w-7 h-7  group ${meditationData?.favorite ? 'text-red-500' : 'text-gray-500 hover:text-red-500'
                                    }`} />
                            ) : (
                                <Skeleton className="h-6 w-6 rounded" />
                            )
                        }
                    </button>
                </div>
            </div>
            <div className="flex flex-col px-6 xl:flex-row">
                {/* Left Section */}
                <div className="w-full px-0 -mt-8 xl:w-2/6 xl:px-6 z-2">
                    {/* <div className="p-2 rounded-lg border-5 border-white-50 bg-gold-50 w-max">
                        <SunIcon className="text-gold-200" />
                    </div> */}
                    <div className="pt-8">
                        <h2 className='pb-2 text-2xl font-bold text-gray-900'> {loading ? (
                            <Skeleton className="h-6 w-48 rounded" />
                        ) : (
                            meditationData?.title || 'Morning Devotion'
                        )}</h2>
                        {loading ? (
                            <Skeleton className="h-44 w-78 rounded" />
                        ) : (
                            <div
                                className="font-normal text-gray-800 prose prose-sm max-w-none"
                                dangerouslySetInnerHTML={{ __html: meditationData?.excerpt || '' }}
                            />
                        )}


                    </div>
                    <div className='my-8'>
                    {audioData.map((audio, index) => (
                        <div
                            key={index}
                            onClick={(e) => {
                                e.preventDefault(); // Prevent default link behavior
                                handlePlay(index); // Play or pause based on the current state
                            }}
                            className="cursor-pointer w-full max-w-[320px] bg-gold-300 rounded-[50px] p-2 flex items-center justify-between my-2"
                        >
                            {/* Play/Pause Button */}
                            <div className="flex items-center justify-center rounded-full bg-gold-200 md:h-11 md:w-11 h-9 w-9">
                                {activePlayerIndex === index ? (
                                    <VoiceSquare progress={progressPercentage / 100} />
                                ) : (
                                    <PlayIcon className="text-gray-0" />
                                )}
                            </div>
                            {/* Audio Title */}
                            <div className="flex items-center justify-center flex-grow select-none">
                                <p className="text-lg font-bold text-gray-0">{audio.title}</p>
                            </div>
                        </div>
                    ))}
                    </div>
                

                    {/* Between left and right sections */}
                    <div className="absolute left-0 right-0 h-px bg-gray-200 max-w-[33.8rem]" />



                    <div className="px-4 mt-16">
                        <h5 className="mb-3 text-base font-semibold leading-5 text-gray-900">
                            Author:
                        </h5>

                        {loading ? (
                            <div className="flex items-center gap-2 mt-4">
                                <Skeleton className="w-8 h-8 rounded-full" />
                                <Skeleton className="h-4 w-32 rounded" />
                            </div>
                        ) : (
                            <div className="flex flex-row items-center gap-2 mb-8">
                                <img src="/favicon.png" alt="Favicon" className="w-7 h-7" />
                                <p className="font-medium text-sm leading-4.5 text-gray-700">
                                    {meditationData?.author?.name || "Unknown Author"}
                                </p>
                            </div>
                        )}
                    </div>
                    {/* Source Section */}
                    {loading ? (
                        <div className="px-4 mt-6">
                            <h5 className="mb-3 text-base font-semibold leading-5 text-gray-900">Source:</h5>
                            <div className="bg-gold-50 p-4 rounded-xl flex items-start gap-4">
                                <Skeleton className="w-12 h-12 rounded" />

                                <div className="flex flex-col gap-1 flex-grow">
                                    <Skeleton className="h-4 w-32 rounded" />
                                    <Skeleton className="h-3 w-24 rounded" />
                                    <div className="mt-2 flex flex-col gap-1">
                                        <Skeleton className="h-3 w-28 rounded" />
                                        <Skeleton className="h-3 w-20 rounded" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        meditationData?.sourceAttribution && meditationData?.sourceUrl && (
                            <div className="px-4 mt-6">
                                <h5 className="mb-3 text-base font-semibold leading-5 text-gray-900">Source:</h5>

                                <div className="bg-gold-50 p-4 rounded-xl flex items-start gap-4">
                                    {/* Image */}
                                    <img
                                        src={`${process.env.PUBLIC_URL}/media/images/tyres.png`}
                                        alt="Source"
                                        className="w-12 h-12 object-contain"
                                    />

                                    {/* Text & Buttons */}
                                    <div className="flex flex-col gap-1">
                                        <p className="text-base font-semibold text-gray-900 leading-tight">Ebook name</p>
                                        <p className="text-xs text-gray-600">{meditationData?.author?.name || "Unknown Author"}</p>

                                        <div className="mt-2 flex flex-col gap-1">
                                            <a
                                                href={meditationData.sourceUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-gold-300 text-sm font-medium font-redhat flex items-center gap-2 hover:text-gold-600 leading-4.5"
                                            >
                                                <img
                                                    src={`${process.env.PUBLIC_URL}/media/images/right-arrow-gold.png`}
                                                    alt="Arrow"
                                                    className="w-[15.33px] h-4"
                                                />
                                                Download eBook
                                                <img
                                                    src={`${process.env.PUBLIC_URL}/media/images/right-arrow-gold.png`}
                                                    alt="Arrow"
                                                    className="w-[15.33px] h-4"
                                                />
                                            </a>

                                            <a
                                                href={meditationData.sourceUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-gold-300 text-sm font-medium flex items-center gap-2 font-redhat hover:text-gold-600 leading-4.5"
                                            >
                                                <img
                                                    src={`${process.env.PUBLIC_URL}/media/images/right-arrow-gold.png`}
                                                    alt="Arrow"
                                                    className="w-[15.33px] h-4"
                                                />
                                                Buy book
                                                <img
                                                    src={`${process.env.PUBLIC_URL}/media/images/right-arrow-gold.png`}
                                                    alt="Arrow"
                                                    className="w-[15.33px] h-4"
                                                />
                                            </a>
                                        </div>

                                    </div>
                                </div>
                            </div>
                        )
                    )}



                </div>
                <div className="border-r border-gray-200 xl:block hidden h-auto mt-[-130px]" />
                {/* Right Section */}
                <div className="w-full pt-6 pl-0 pr-0 xl:w-4/6 xl:pr-6 xl:pl-12">
                    {loading ? (
                        <div className="space-y-4">
                            <Skeleton className="h-96 mt-6 text-gray-900 text-base leading-7 space-y-5 max-w-[650px]" />

                        </div>
                    ) : (
                        <>

                            {/* Content Section */}
                            <div
                                className="mt-6 text-gray-900 text-base leading-7 space-y-5 max-w-[650px] my-audio-content"
                                dangerouslySetInnerHTML={{ __html: meditationData?.content || '' }}
                            />
                        </>
                    )}
                </div>

            </div>
            <div className='fixed bottom-0 xl:left-[16.66%] md:left-[15rem] left-0 right-0 z-50'>
                {/* Conditionally render AudioPlayer with animation */}
                <div
                    className={`transition-transform duration-500 ease-out transform bg-gray-0 ${activePlayerIndex !== null ? "animate-slideUp" : "animate-slideDown"
                        }`}
                >
                    {/* {activePlayerIndex !== null && (
                        <AudioPlayer
                            src={audioData[activePlayerIndex].src}
                            title={audioData[activePlayerIndex].title}
                            excerpt={audioData[activePlayerIndex].excerpt}
                            onClose={handleCloseAudioPlayer}
                            audioRef={audioRef}
                            onProgressUpdate={handleProgressUpdate}
                        // Pass the ref to control audio
                        />
                    )} */}
                    {showPlayer && typeof meditationData?.audio === "string" && (
                        <div className="mt-4">
                            <AudioPlayer
                                src={meditationData.audio}
                                title={meditationData.title}
                                excerpt="Meditation Audio"
                                onClose={() => setShowPlayer(false)}
                                audioRef={audioRef}
                                onProgressUpdate={setProgressPercentage}
                            />
                        </div>
                    )}

                    {showTrinity && (
                        <div className="mt-4">
                            <TrinityAudioPlayer selector=".my-audio-content" langKey="en" />
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default Meditation